// src/pages/AutomationCommandCenter.jsx
// Founder-only Automation Command Center
// Displays: Kill switches, Today's stats, OEP blast dashboard, Queue management, Activity log, Test sends

import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Zap,
  Power,
  MessageSquare,
  Mail,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
  RefreshCw,
  Send,
  ChevronDown,
  ChevronRight,
  Activity,
  Calendar,
  Pause,
  Play,
  TestTube,
  Trash2,
  RotateCcw,
  List,
  Eye,
  X
} from "lucide-react";
import {
  getKillSwitchStates,
  toggleGlobalKillSwitch,
  toggleChannelKillSwitch,
  toggleTriggerKillSwitch,
  getAutomationStatsToday,
  getOEPBlastStats,
  getAutomationQueue,
  getAutomationActivity,
  getAutomationTriggers,
  sendAutomationTest,
  forceProcessQueue,
  clearAutomationQueue,
  requeueAutomation
} from "../api";

export default function AutomationCommandCenter() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  // Data states
  const [killSwitch, setKillSwitch] = useState(null);
  const [todayStats, setTodayStats] = useState(null);
  const [oepStats, setOepStats] = useState(null);
  const [activity, setActivity] = useState([]);
  const [triggers, setTriggers] = useState([]);

  // UI states
  const [expandedSections, setExpandedSections] = useState({
    killSwitch: true,
    triggers: false,
    oep: true,
    queueManagement: true,
    activity: true
  });
  const [testTrigger, setTestTrigger] = useState('birthday');
  const [testChannel, setTestChannel] = useState('sms');
  const [testLoading, setTestLoading] = useState(false);

  // Queue management states
  const [queueMessages, setQueueMessages] = useState([]);
  const [queueLoading, setQueueLoading] = useState(false);
  const [queueFilter, setQueueFilter] = useState('');
  const [queueVisible, setQueueVisible] = useState(true);
  const [clearingQueue, setClearingQueue] = useState(false);
  const [requeueing, setRequeueing] = useState(false);
  const [queueTab, setQueueTab] = useState('sms');
  const [queueStatus, setQueueStatus] = useState(null); // { type: 'success'|'error'|'info', message: string }

  // Toast notification state
  const [toasts, setToasts] = useState([]);
  const toastIdRef = useRef(0);

  // Auto-refresh timer for post-operation polling
  const pollRef = useRef(null);

  const addToast = useCallback((type, message) => {
    const id = ++toastIdRef.current;
    setToasts(prev => [...prev, { id, type, message, timestamp: Date.now() }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 8000);
    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const loadData = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    else setLoading(true);

    try {
      const [killRes, statsRes, oepRes, activityRes, triggersRes] = await Promise.all([
        getKillSwitchStates().catch(() => null),
        getAutomationStatsToday().catch(() => null),
        getOEPBlastStats(new Date().getFullYear()).catch(() => null),
        getAutomationActivity(24).catch(() => ({ activity: [] })),
        getAutomationTriggers().catch(() => ({ triggers: [] }))
      ]);

      if (killRes?.ok) setKillSwitch(killRes.killSwitch);
      if (statsRes?.ok) setTodayStats(statsRes);
      if (oepRes?.ok) setOepStats(oepRes);
      if (activityRes?.ok) setActivity(activityRes.activity || []);
      if (triggersRes?.ok) setTriggers(triggersRes.triggers || []);

      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  const loadQueueRef = useRef(null);
  loadQueueRef.current = queueFilter;

  const loadQueueMessages = useCallback(async (filterKey) => {
    const key = filterKey !== undefined ? filterKey : loadQueueRef.current;
    setQueueLoading(true);
    try {
      const options = { status: 'queued', limit: 500 };
      if (key) options.automation_key = key;
      const result = await getAutomationQueue(options);
      if (result.ok) {
        setQueueMessages(result.messages || []);
      }
    } catch (err) {
      // Silent fail on background refresh
    } finally {
      setQueueLoading(false);
    }
  }, []);

  // Refresh data + queue after an operation, with rapid polls to catch async ops
  const refreshAfterOperation = useCallback(() => {
    loadData(true);
    loadQueueMessages();
    let polls = 0;
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(() => {
      polls++;
      loadData(true);
      loadQueueMessages();
      if (polls >= 4) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    }, 5000);
  }, [loadData, loadQueueMessages]);

  useEffect(() => {
    loadData();
    loadQueueMessages();
    const interval = setInterval(() => {
      loadData(true);
      loadQueueMessages();
    }, 30000);
    return () => {
      clearInterval(interval);
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [loadData, loadQueueMessages]);

  const handleToggleGlobal = async () => {
    if (!killSwitch) return;
    const newState = !killSwitch.global.is_paused;
    const reason = newState ? 'Paused from Command Center' : 'Resumed from Command Center';

    try {
      await toggleGlobalKillSwitch(newState, reason);
      addToast('success', newState ? 'All automations PAUSED' : 'All automations RESUMED');
      loadData(true);
    } catch (err) {
      addToast('error', `Failed to toggle: ${err.message}`);
    }
  };

  const handleToggleChannel = async (channel) => {
    const channelData = killSwitch?.channels?.find(c => c.scope_value === channel);
    const newState = !channelData?.is_paused;
    const reason = newState ? `${channel.toUpperCase()} paused from Command Center` : `${channel.toUpperCase()} resumed from Command Center`;

    try {
      await toggleChannelKillSwitch(channel, newState, reason);
      addToast('success', newState ? `${channel.toUpperCase()} channel PAUSED` : `${channel.toUpperCase()} channel RESUMED`);
      loadData(true);
    } catch (err) {
      addToast('error', `Failed to toggle: ${err.message}`);
    }
  };

  const handleToggleTrigger = async (triggerKey) => {
    const trigger = triggers.find(t => t.key === triggerKey);
    const newState = !trigger?.is_paused;
    const reason = newState ? 'Paused from Command Center' : 'Resumed from Command Center';

    try {
      await toggleTriggerKillSwitch(triggerKey, newState, reason);
      addToast('success', `${trigger?.name || triggerKey} ${newState ? 'PAUSED' : 'RESUMED'}`);
      loadData(true);
    } catch (err) {
      addToast('error', `Failed to toggle: ${err.message}`);
    }
  };

  const handleSendTest = async () => {
    setTestLoading(true);
    try {
      const result = await sendAutomationTest(testTrigger, testChannel, null, true);
      if (result.ok) {
        addToast('success', `Test ${testChannel.toUpperCase()} sent to ${result.recipient}`);
      } else {
        addToast('error', `Test failed: ${result.error}`);
      }
    } catch (err) {
      addToast('error', `Test error: ${err.message}`);
    } finally {
      setTestLoading(false);
    }
  };

  const handleForceProcess = async () => {
    if (!confirm('Force process all queued messages now? This will send ALL queued messages immediately.')) return;
    setQueueStatus({ type: 'info', message: 'Processing queue — sending all queued messages...' });
    addToast('info', 'Processing queue...');
    try {
      const result = await forceProcessQueue();
      const msg = `Done! ${result.result?.sent || 0} sent, ${result.result?.failed || 0} failed`;
      setQueueStatus({ type: 'success', message: msg });
      addToast('success', msg);
      refreshAfterOperation();
    } catch (err) {
      const msg = `Process error: ${err.message}`;
      setQueueStatus({ type: 'error', message: msg });
      addToast('error', msg);
    }
  };

  const handleViewQueue = () => {
    const next = !queueVisible;
    setQueueVisible(next);
    if (next) loadQueueMessages();
  };

  const handleClearQueue = async () => {
    if (!queueFilter) {
      setQueueStatus({ type: 'error', message: 'Select an automation type from the dropdown first.' });
      addToast('error', 'Select an automation type first');
      return;
    }
    const smsCount = queueMessages.filter(m => m.automation_key === queueFilter && m.channel === 'sms').length;
    const emailCount = queueMessages.filter(m => m.automation_key === queueFilter && m.channel === 'email').length;
    if (!confirm(
      `Cancel all queued "${formatTriggerName(queueFilter)}" messages?\n\n${smsCount} SMS + ${emailCount} Email = ${smsCount + emailCount} total\n\nThis cannot be undone.`
    )) return;

    setClearingQueue(true);
    setQueueStatus({ type: 'info', message: `Clearing ${formatTriggerName(queueFilter)} queue...` });
    addToast('info', `Clearing ${formatTriggerName(queueFilter)} queue...`);
    try {
      const result = await clearAutomationQueue(queueFilter);
      const msg = `Cleared ${result.cancelled} queued messages for ${formatTriggerName(queueFilter)}`;
      setQueueStatus({ type: 'success', message: msg });
      addToast('success', msg);
      refreshAfterOperation();
    } catch (err) {
      const msg = `Clear failed: ${err.message}`;
      setQueueStatus({ type: 'error', message: msg });
      addToast('error', msg);
    } finally {
      setClearingQueue(false);
    }
  };

  const handleRequeue = async () => {
    const oepKeys = ['oep_jan2', 'oep_feb1', 'oep_mar1'];
    const targetKey = queueFilter && oepKeys.includes(queueFilter) ? queueFilter : null;

    if (!targetKey) {
      setQueueStatus({ type: 'error', message: 'Select an OEP automation (Jan 2, Feb 1, or Mar 1) from the dropdown to re-queue.' });
      addToast('error', 'Select an OEP automation (Jan 2, Feb 1, or Mar 1) to re-queue');
      return;
    }

    if (!confirm(
      `Re-queue "${formatTriggerName(targetKey)}"?\n\nThis will:\n1. Cancel all currently queued messages\n2. Reset today's sent flags\n3. Re-run with fresh templates\n\nNew messages will appear in the queue shortly.`
    )) return;

    setRequeueing(true);
    setQueueStatus({ type: 'info', message: `Re-queuing ${formatTriggerName(targetKey)} — clearing old messages and re-populating with fresh templates...` });
    addToast('info', `Re-queuing ${formatTriggerName(targetKey)}...`);
    try {
      const result = await requeueAutomation(targetKey);
      const msg = `Re-queue started! Cancelled ${result.cancelled} old messages, reset ${result.cohortReset} cohort flags. New messages populating now.`;
      setQueueStatus({ type: 'success', message: msg });
      addToast('success', msg);
      refreshAfterOperation();
    } catch (err) {
      const msg = `Re-queue failed: ${err.message}`;
      setQueueStatus({ type: 'error', message: msg });
      addToast('error', msg);
    } finally {
      setRequeueing(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <RefreshCw className="animate-spin text-blue-600" size={32} />
      </div>
    );
  }

  const globalPaused = killSwitch?.global?.is_paused;
  const smsPaused = killSwitch?.channels?.find(c => c.scope_value === 'sms')?.is_paused;
  const emailPaused = killSwitch?.channels?.find(c => c.scope_value === 'email')?.is_paused;

  const smsQueueCount = queueMessages.filter(m => m.channel === 'sms').length;
  const emailQueueCount = queueMessages.filter(m => m.channel === 'email').length;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">

        {/* Toast Notifications */}
        {toasts.length > 0 && (
          <div className="fixed top-4 right-4 z-50 space-y-2 max-w-md">
            {toasts.map(toast => (
              <div
                key={toast.id}
                className={`flex items-start gap-3 p-4 rounded-lg shadow-lg border animate-in slide-in-from-right ${
                  toast.type === 'success' ? 'bg-green-50 border-green-300 text-green-800' :
                  toast.type === 'error' ? 'bg-red-50 border-red-300 text-red-800' :
                  'bg-blue-50 border-blue-300 text-blue-800'
                }`}
              >
                {toast.type === 'success' ? <CheckCircle2 size={18} className="mt-0.5 flex-shrink-0" /> :
                 toast.type === 'error' ? <XCircle size={18} className="mt-0.5 flex-shrink-0" /> :
                 <RefreshCw size={18} className="mt-0.5 flex-shrink-0 animate-spin" />}
                <p className="text-sm flex-1">{toast.message}</p>
                <button onClick={() => removeToast(toast.id)} className="flex-shrink-0 hover:opacity-70">
                  <X size={16} />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Zap className="text-amber-500" size={28} />
            <h1 className="text-2xl font-bold text-gray-900">Automation Command Center</h1>
          </div>
          <button
            onClick={() => { loadData(true); loadQueueMessages(); }}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 bg-white border rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}

        {/* Hero Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <StatCard
            icon={<Send className="text-blue-500" />}
            label="Today's Sends"
            value={todayStats?.summary?.sent || 0}
            change={todayStats?.summary?.changeFromYesterday}
          />
          <StatCard
            icon={<Clock className="text-amber-500" />}
            label="Pending"
            value={todayStats?.summary?.pending || 0}
          />
          <StatCard
            icon={<CheckCircle2 className="text-green-500" />}
            label="Success Rate"
            value={todayStats?.summary?.total > 0
              ? `${Math.round((todayStats.summary.sent / todayStats.summary.total) * 100)}%`
              : 'N/A'
            }
          />
          <StatCard
            icon={globalPaused ? <Pause className="text-red-500" /> : <Play className="text-green-500" />}
            label="System Status"
            value={globalPaused ? 'PAUSED' : 'Active'}
            className={globalPaused ? 'border-red-200 bg-red-50' : 'border-green-200 bg-green-50'}
          />
        </div>

        {/* Kill Switch Section */}
        <Section
          title="Kill Switch Controls"
          icon={<Power size={20} />}
          expanded={expandedSections.killSwitch}
          onToggle={() => toggleSection('killSwitch')}
          className="mb-6"
        >
          <div className="space-y-4">
            {/* Global */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <AlertTriangle className={globalPaused ? 'text-red-500' : 'text-gray-400'} size={20} />
                <div>
                  <span className="font-medium">GLOBAL PAUSE</span>
                  <p className="text-sm text-gray-500">Stop ALL automations</p>
                </div>
              </div>
              <ToggleSwitch
                checked={!globalPaused}
                onChange={handleToggleGlobal}
                dangerous={true}
              />
            </div>

            {/* Channels */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center gap-3">
                  <MessageSquare className={smsPaused ? 'text-red-500' : 'text-blue-500'} size={20} />
                  <span className="font-medium">SMS Channel</span>
                </div>
                <ToggleSwitch
                  checked={!smsPaused}
                  onChange={() => handleToggleChannel('sms')}
                />
              </div>
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center gap-3">
                  <Mail className={emailPaused ? 'text-red-500' : 'text-blue-500'} size={20} />
                  <span className="font-medium">Email Channel</span>
                </div>
                <ToggleSwitch
                  checked={!emailPaused}
                  onChange={() => handleToggleChannel('email')}
                />
              </div>
            </div>
          </div>
        </Section>

        {/* Triggers Section */}
        <Section
          title={`Triggers (${triggers.length})`}
          icon={<Zap size={20} />}
          expanded={expandedSections.triggers}
          onToggle={() => toggleSection('triggers')}
          className="mb-6"
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left p-3">Trigger</th>
                  <th className="text-left p-3">Category</th>
                  <th className="text-left p-3">Channels</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-right p-3">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {triggers.map(trigger => (
                  <tr key={trigger.key} className="hover:bg-gray-50">
                    <td className="p-3 font-medium">{trigger.name}</td>
                    <td className="p-3 text-gray-600">{trigger.category}</td>
                    <td className="p-3">
                      <div className="flex gap-1">
                        {trigger.channels.map(ch => (
                          <span key={ch} className="px-2 py-0.5 bg-gray-100 rounded text-xs">
                            {ch.toUpperCase()}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="p-3">
                      {trigger.is_paused ? (
                        <span className="flex items-center gap-1 text-red-600">
                          <Pause size={14} /> Paused
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-green-600">
                          <Play size={14} /> Active
                        </span>
                      )}
                    </td>
                    <td className="p-3 text-right">
                      <button
                        onClick={() => handleToggleTrigger(trigger.key)}
                        className={`px-3 py-1 rounded text-sm ${
                          trigger.is_paused
                            ? 'bg-green-100 text-green-700 hover:bg-green-200'
                            : 'bg-red-100 text-red-700 hover:bg-red-200'
                        }`}
                      >
                        {trigger.is_paused ? 'Resume' : 'Pause'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>

        {/* Today's Breakdown */}
        <Section
          title="Today's Automation Breakdown"
          icon={<Activity size={20} />}
          expanded={true}
          onToggle={() => {}}
          className="mb-6"
        >
          {todayStats?.byTrigger?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left p-3">Trigger</th>
                    <th className="text-right p-3">Queued</th>
                    <th className="text-right p-3">Sent</th>
                    <th className="text-right p-3">Failed</th>
                    <th className="text-right p-3">Skipped</th>
                    <th className="text-right p-3">Cancelled</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {todayStats.byTrigger.map(row => (
                    <tr key={row.automation_key} className="hover:bg-gray-50">
                      <td className="p-3 font-medium">{formatTriggerName(row.automation_key)}</td>
                      <td className="p-3 text-right">{row.queued}</td>
                      <td className="p-3 text-right text-green-600">{row.sent}</td>
                      <td className="p-3 text-right text-red-600">{row.failed || 0}</td>
                      <td className="p-3 text-right text-gray-500">{row.skipped || 0}</td>
                      <td className="p-3 text-right text-gray-500">{row.cancelled || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No automations processed today yet</p>
          )}
        </Section>

        {/* OEP Blast Dashboard */}
        <Section
          title={`OEP Blast Dashboard - ${oepStats?.year || new Date().getFullYear()}`}
          icon={<Calendar size={20} />}
          expanded={expandedSections.oep}
          onToggle={() => toggleSection('oep')}
          className="mb-6"
        >
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
            <div className="p-4 bg-gray-50 rounded-lg text-center">
              <div className="text-2xl font-bold text-gray-900">{oepStats?.cohort?.total || 0}</div>
              <div className="text-sm text-gray-600">Total Cohort</div>
            </div>
            <div className="p-4 bg-blue-50 rounded-lg text-center">
              <div className="text-2xl font-bold text-blue-700">{oepStats?.cohort?.jan2Sent || 0}</div>
              <div className="text-sm text-blue-600">Jan 2 Sent</div>
            </div>
            <div className="p-4 bg-blue-50 rounded-lg text-center">
              <div className="text-2xl font-bold text-blue-700">{oepStats?.cohort?.feb1Sent || 0}</div>
              <div className="text-sm text-blue-600">Feb 1 Sent</div>
            </div>
            <div className="p-4 bg-blue-50 rounded-lg text-center">
              <div className="text-2xl font-bold text-blue-700">{oepStats?.cohort?.mar1Sent || 0}</div>
              <div className="text-sm text-blue-600">Mar 1 Sent</div>
            </div>
          </div>
        </Section>

        {/* Queue Management */}
        <Section
          title="Queue Management"
          icon={<List size={20} />}
          expanded={expandedSections.queueManagement}
          onToggle={() => toggleSection('queueManagement')}
          className="mb-6"
        >
          {/* Queue Summary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-4">
            <div className="p-3 bg-amber-50 rounded-lg text-center">
              <div className="text-xl font-bold text-amber-700">{todayStats?.summary?.pending || 0}</div>
              <div className="text-xs text-amber-600">Queued</div>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg text-center">
              <div className="text-xl font-bold text-blue-700">{smsQueueCount}</div>
              <div className="text-xs text-blue-600">SMS Queued</div>
            </div>
            <div className="p-3 bg-purple-50 rounded-lg text-center">
              <div className="text-xl font-bold text-purple-700">{emailQueueCount}</div>
              <div className="text-xs text-purple-600">Email Queued</div>
            </div>
            <div className="p-3 bg-green-50 rounded-lg text-center">
              <div className="text-xl font-bold text-green-700">{todayStats?.summary?.sent || 0}</div>
              <div className="text-xs text-green-600">Sent</div>
            </div>
            <div className="p-3 bg-red-50 rounded-lg text-center">
              <div className="text-xl font-bold text-red-700">{todayStats?.summary?.failed || 0}</div>
              <div className="text-xs text-red-600">Failed</div>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg text-center">
              <div className="text-xl font-bold text-gray-700">{todayStats?.summary?.skipped || 0}</div>
              <div className="text-xs text-gray-600">Skipped</div>
            </div>
          </div>

          {/* Filter + Action Buttons */}
          <div className="flex flex-wrap items-end gap-3 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Automation</label>
              <select
                value={queueFilter}
                onChange={(e) => {
                  setQueueFilter(e.target.value);
                  loadQueueMessages(e.target.value);
                }}
                className="px-3 py-2 border rounded-lg"
              >
                <option value="">All Automations</option>
                {triggers.map(t => (
                  <option key={t.key} value={t.key}>{t.name}</option>
                ))}
              </select>
            </div>

            <button
              onClick={handleViewQueue}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <Eye size={16} />
              {queueVisible ? 'Hide Queue' : 'View Queue'}
            </button>

            <button
              onClick={handleClearQueue}
              disabled={clearingQueue}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
            >
              {clearingQueue ? <RefreshCw size={16} className="animate-spin" /> : <Trash2 size={16} />}
              Clear Queue
            </button>

            <button
              onClick={handleRequeue}
              disabled={requeueing}
              className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 flex items-center gap-2"
            >
              {requeueing ? <RefreshCw size={16} className="animate-spin" /> : <RotateCcw size={16} />}
              Re-queue OEP
            </button>

            <button
              onClick={handleForceProcess}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
            >
              <Zap size={16} />
              Force Process Now
            </button>
          </div>

          {/* Inline Status Banner */}
          {queueStatus && (
            <div className={`flex items-center gap-3 p-4 rounded-lg mb-4 ${
              queueStatus.type === 'success' ? 'bg-green-100 border-2 border-green-400 text-green-800' :
              queueStatus.type === 'error' ? 'bg-red-100 border-2 border-red-400 text-red-800' :
              'bg-blue-100 border-2 border-blue-400 text-blue-800'
            }`}>
              {queueStatus.type === 'success' ? <CheckCircle2 size={20} className="flex-shrink-0" /> :
               queueStatus.type === 'error' ? <XCircle size={20} className="flex-shrink-0" /> :
               <RefreshCw size={20} className="flex-shrink-0 animate-spin" />}
              <p className="font-medium flex-1">{queueStatus.message}</p>
              <button onClick={() => setQueueStatus(null)} className="flex-shrink-0 hover:opacity-70">
                <X size={18} />
              </button>
            </div>
          )}

          {/* Queue Messages Table */}
          {queueVisible && (
            <div>
              {queueLoading && queueMessages.length === 0 ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw size={20} className="animate-spin text-blue-600" />
                  <span className="ml-2 text-gray-500">Loading queue...</span>
                </div>
              ) : queueMessages.length > 0 ? (
                <>
                  {/* SMS / Email Tabs */}
                  {(() => {
                    const smsMessages = queueMessages.filter(m => m.channel === 'sms');
                    const emailMessages = queueMessages.filter(m => m.channel === 'email');
                    const activeMessages = queueTab === 'sms' ? smsMessages : emailMessages;

                    return (
                      <>
                        <div className="flex border-b mb-3">
                          <button
                            onClick={() => setQueueTab('sms')}
                            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                              queueTab === 'sms'
                                ? 'border-blue-600 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                          >
                            <MessageSquare size={14} />
                            SMS ({smsMessages.length})
                          </button>
                          <button
                            onClick={() => setQueueTab('email')}
                            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                              queueTab === 'email'
                                ? 'border-purple-600 text-purple-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                          >
                            <Mail size={14} />
                            Email ({emailMessages.length})
                          </button>
                          {queueLoading && (
                            <div className="ml-auto flex items-center px-3 text-gray-400">
                              <RefreshCw size={14} className="animate-spin" />
                            </div>
                          )}
                        </div>

                        {activeMessages.length > 0 ? (
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="text-left p-3">Agent</th>
                                  <th className="text-left p-3">Client</th>
                                  <th className="text-left p-3">Automation</th>
                                  <th className="text-left p-3">Status</th>
                                  <th className="text-left p-3">Scheduled For</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y">
                                {activeMessages.map(msg => (
                                  <tr key={msg.id} className="hover:bg-gray-50">
                                    <td className="p-3">{msg.agent_name || 'Unknown'}</td>
                                    <td className="p-3">{msg.recipient_name || 'Unknown'}</td>
                                    <td className="p-3">{formatTriggerName(msg.automation_key)}</td>
                                    <td className="p-3">
                                      <span className="px-2 py-0.5 rounded text-xs bg-amber-100 text-amber-700">
                                        {msg.status}
                                      </span>
                                    </td>
                                    <td className="p-3 text-gray-600">
                                      {msg.scheduled_for ? new Date(msg.scheduled_for).toLocaleString() : '---'}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            <p className="text-sm text-gray-500 mt-2 px-3">
                              Showing {activeMessages.length} {queueTab.toUpperCase()} message{activeMessages.length !== 1 ? 's' : ''}
                              {' '}({queueMessages.length} total queued)
                            </p>
                          </div>
                        ) : (
                          <p className="text-gray-500 text-center py-6">
                            No {queueTab.toUpperCase()} messages queued{queueFilter ? ` for ${formatTriggerName(queueFilter)}` : ''}
                          </p>
                        )}
                      </>
                    );
                  })()}
                </>
              ) : (
                <p className="text-gray-500 text-center py-8">
                  No queued messages found{queueFilter ? ` for ${formatTriggerName(queueFilter)}` : ''}
                </p>
              )}
            </div>
          )}
        </Section>

        {/* Test Send */}
        <Section
          title="Test Automation"
          icon={<TestTube size={20} />}
          expanded={true}
          onToggle={() => {}}
          className="mb-6"
        >
          <div className="flex items-end gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Trigger</label>
              <select
                value={testTrigger}
                onChange={(e) => setTestTrigger(e.target.value)}
                className="px-3 py-2 border rounded-lg"
              >
                {triggers.map(t => (
                  <option key={t.key} value={t.key}>{t.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Channel</label>
              <select
                value={testChannel}
                onChange={(e) => setTestChannel(e.target.value)}
                className="px-3 py-2 border rounded-lg"
              >
                <option value="sms">SMS</option>
                <option value="email">Email</option>
              </select>
            </div>
            <button
              onClick={handleSendTest}
              disabled={testLoading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              {testLoading ? <RefreshCw size={16} className="animate-spin" /> : <Send size={16} />}
              Send Test to Myself
            </button>
          </div>
        </Section>

        {/* Activity Log */}
        <Section
          title="Recent Activity (24h)"
          icon={<Activity size={20} />}
          expanded={expandedSections.activity}
          onToggle={() => toggleSection('activity')}
          className="mb-6"
        >
          <div className="max-h-96 overflow-y-auto">
            {activity.length > 0 ? (
              <div className="space-y-2">
                {activity.slice(0, 50).map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg text-sm">
                    <ActivityIcon status={item.status} type={item.activity_type} />
                    <div className="flex-1">
                      <p className="text-gray-900">{item.description}</p>
                      <p className="text-gray-500 text-xs mt-1">
                        {new Date(item.timestamp).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No recent activity</p>
            )}
          </div>
        </Section>
      </div>
    </div>
  );
}

// Helper Components
function StatCard({ icon, label, value, change, className = '' }) {
  return (
    <div className={`p-4 bg-white border rounded-lg ${className}`}>
      <div className="flex items-center gap-3">
        {icon}
        <div>
          <div className="text-2xl font-bold">{value}</div>
          <div className="text-sm text-gray-600 flex items-center gap-1">
            {label}
            {change !== undefined && (
              <span className={change >= 0 ? 'text-green-600' : 'text-red-600'}>
                ({change >= 0 ? '+' : ''}{change})
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, icon, expanded, onToggle, children, className = '' }) {
  return (
    <div className={`bg-white border rounded-lg ${className}`}>
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50"
      >
        <div className="flex items-center gap-2 font-semibold">
          {icon}
          {title}
        </div>
        {expanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
      </button>
      {expanded && <div className="p-4 pt-0">{children}</div>}
    </div>
  );
}

function ToggleSwitch({ checked, onChange, dangerous = false }) {
  return (
    <button
      onClick={onChange}
      className={`relative w-12 h-6 rounded-full transition-colors ${
        checked
          ? dangerous ? 'bg-green-500' : 'bg-blue-500'
          : 'bg-gray-300'
      }`}
    >
      <span
        className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
          checked ? 'translate-x-7' : 'translate-x-1'
        }`}
      />
    </button>
  );
}

function ActivityIcon({ status, type }) {
  if (type === 'kill_switch') {
    return <Power size={16} className="text-amber-500 mt-0.5" />;
  }

  switch (status) {
    case 'sent':
      return <CheckCircle2 size={16} className="text-green-500 mt-0.5" />;
    case 'failed':
      return <XCircle size={16} className="text-red-500 mt-0.5" />;
    case 'skipped':
    case 'cancelled':
      return <Clock size={16} className="text-gray-400 mt-0.5" />;
    default:
      return <Clock size={16} className="text-blue-500 mt-0.5" />;
  }
}

function formatTriggerName(key) {
  const names = {
    birthday: 'Birthday',
    welcome_day_1: 'Day 1 Welcome',
    welcome_day_30: 'Day 30 Follow-up',
    welcome_day_90: 'Day 90 Review',
    oep_jan2: 'OEP Jan 2',
    oep_feb1: 'OEP Feb 1',
    oep_mar1: 'OEP Mar 1',
    policy_anniversary: 'Policy Anniversary',
    anoc_annual_review: 'ANOC & Annual Review'
  };
  return names[key] || key?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown';
}
